import React from "react";
import Markdown from "markdown-to-jsx";
import Latex from "react-latex-next";
import "katex/dist/katex.min.css";
import "./MessageContent.css";

function protectCodeSegments(content) {
  const protectedSegments = [];
  const protectedContent = content.replace(/```[\s\S]*?```|`[^`\n]*`/g, (match) => {
    const token = `@@CODE_SEGMENT_${protectedSegments.length}@@`;
    protectedSegments.push(match);
    return token;
  });

  return { protectedContent, protectedSegments };
}

function restoreCodeSegments(content, protectedSegments) {
  return content.replace(/@@CODE_SEGMENT_(\d+)@@/g, (_match, index) => protectedSegments[Number(index)] || "");
}

function encodeFormula(formula) {
  return encodeURIComponent(formula);
}

function normalizeContent(content) {
  if (content === null || content === undefined) {
    return "";
  }

  const normalized = String(content).replace(/\r\n/g, "\n");
  const { protectedContent, protectedSegments } = protectCodeSegments(normalized);

  const withMathPlaceholders = protectedContent
    .replace(/\\\[((?:[\s\S]*?))\\\]/g, (_match, inner) => {
      return `\n<latex-block data-formula="${encodeFormula(`$$${inner}$$`)}"></latex-block>\n`;
    })
    .replace(/\\\(((?:[\s\S]*?))\\\)/g, (_match, inner) => {
      return `<latex-inline data-formula="${encodeFormula(`$${inner}$`)}"></latex-inline>`;
    })
    .replace(/\$\$([\s\S]+?)\$\$/g, (_match, inner) => {
      return `\n<latex-block data-formula="${encodeFormula(`$$${inner}$$`)}"></latex-block>\n`;
    })
    .replace(/\$([^$\n]+?)\$/g, (_match, inner) => {
      return `<latex-inline data-formula="${encodeFormula(`$${inner}$`)}"></latex-inline>`;
    });

  return restoreCodeSegments(withMathPlaceholders, protectedSegments);
}

function decodeFormula(value = "") {
  return decodeURIComponent(value);
}

function stripMathDelimiters(formula = "") {
  if (formula.startsWith("$$") && formula.endsWith("$$")) {
    return formula.slice(2, -2);
  }
  if (formula.startsWith("$") && formula.endsWith("$")) {
    return formula.slice(1, -1);
  }
  return formula;
}

function looksLikeActualMath(formula = "") {
  const inner = stripMathDelimiters(formula).trim();
  if (!inner) {
    return false;
  }

  const hasExplicitMathSyntax = /[=+\-*/^_{}<>]|\\[A-Za-z]+|\d+\s*[+\-*/=<>]\s*\d+/.test(inner);
  if (hasExplicitMathSyntax) {
    return true;
  }

  const wordCount = (inner.match(/[A-Za-z]+/g) || []).length;
  const whitespaceCount = (inner.match(/\s/g) || []).length;

  // Long natural-language spans inside math delimiters render as italicized,
  // space-stripped prose in KaTeX. Treat those as plain text instead.
  if (wordCount >= 4 && whitespaceCount >= 3) {
    return false;
  }

  return /^[A-Za-z0-9.,()]+$/.test(inner) === false;
}

function LatexInline({ "data-formula": formula }) {
  const decodedFormula = decodeFormula(formula);
  if (!looksLikeActualMath(decodedFormula)) {
    return <span>{stripMathDelimiters(decodedFormula)}</span>;
  }

  return (
    <Latex className="message-latex" output="html">
      {decodedFormula}
    </Latex>
  );
}

function LatexBlock({ "data-formula": formula }) {
  const decodedFormula = decodeFormula(formula);
  if (!looksLikeActualMath(decodedFormula)) {
    return <span>{stripMathDelimiters(decodedFormula)}</span>;
  }

  return (
    <span className="message-latex-block">
      <Latex className="message-latex" output="html">
        {decodedFormula}
      </Latex>
    </span>
  );
}

function MessageContent({ content }) {
  const normalizedContent = normalizeContent(content);

  return (
    <div className="message-markdown">
      <Markdown
        options={{
          forceBlock: true,
          overrides: {
            code: {
              props: {
                className: "message-inline-code",
              },
            },
            "latex-inline": {
              component: LatexInline,
            },
            "latex-block": {
              component: LatexBlock,
            },
            pre: {
              props: {
                className: "message-code-block",
              },
            },
          },
        }}
      >
        {normalizedContent}
      </Markdown>
    </div>
  );
}

export default MessageContent;
